import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { endsWith, find, isEmpty, omit, snakeCase } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import { graphql } from "@/gql";
import { Maybe, ViewBodyCompositionModalFragment } from "@/gql/graphql";
import useAlert from "@/hooks/useAlert";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { percent } from "@/utils/number";

import AddBodyCompositionModal from "./AddBodyCompositionModal";
import OptionMenu from "./OptionMenu";
import ViewBodyCompositionModal from "./ViewBodyCompositionModal";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    backgroundColor: colors.white,
    flexDirection: "column",
    margin: theme.spacing(0, 2, 4, 2),
  },
  header: {
    fontSize: theme.typography.pxToRem(16),
    margin: theme.spacing(4, 4),
  },
  headerText: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    margin: theme.spacing(4),
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    margin: theme.spacing(0, 4),
  },
  tableContainer: {
    display: "flex",
    alignItems: "center",
    overflowX: "auto",
    overFlowY: "scroll",
    width: "100%",
  },
  table: {
    borderCollapse: "separate",
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  dateLabel: {
    minWidth: "150px",
  },
  stickyLeftCell: {
    position: "sticky",
    left: 0,
    borderRight: "4px solid #DEE6F0 !important",
  },
  moreCell: {
    borderLeft: "4px solid #DEE6F0 !important",
  },
  cellLabel: {
    fontWeight: 700,
  },
  moreLabel: {
    minWidth: "80px",
    color: "#1FD4AF !important",
  },
  moreButton: {
    color: colors.primary,
    padding: "8px",
    border: "1px solid #1FD4AF",
  },
}));

const StyledTableCell = withStyles(TableCell, () => ({
  root: {
    border: "1px solid #EDF0F5",
    backgroundColor: colors.white,
  },
}));

const StyledTableCellHead = withStyles(TableCell, (theme: Theme) => ({
  root: {
    backgroundColor: "#EDF0F5",
    border: "1px solid #DEE6F0",
    color: colors.gray,
    height: "2px",
    padding: theme.spacing(1, 2),
  },
}));

const StyledTableHeadRow = withStyles(TableRow, (theme: Theme) => ({
  root: {
    height: theme.typography.pxToRem(0.1),
  },
}));

const StyledTableHead = withStyles(TableHead, () => ({
  root: {
    backgroundColor: colors.white,
    height: ".1rem",
  },
}));

const StyledTableRow = withStyles(TableRow, (theme: Theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const StyledButton = withStyles(Button, (theme: Theme) => ({
  root: {
    height: theme.typography.pxToRem(32),
    width: theme.typography.pxToRem(63),
    marginRight: 8,
    border: "2px solid #1FD4AF",
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(2),
  },
  disabled: {
    border: 0,
  },
}));

type FieldType = "defaultField" | "additionalField";

type Field = {
  [key: string]: FieldType;
};

const FIELDS: Field = {
  bodyFatPercentage: "defaultField",
  boneMassKg: "defaultField",
  fatFreeMassKg: "defaultField",
  fatFreeMassPercentage: "defaultField",
  fatMassKg: "defaultField",
  leanBodyMassKg: "defaultField",
  leanBodyMassPercentage: "defaultField",
  metabolicAge: "defaultField",
  muscleMassKg: "defaultField",
  muscleMassPercentage: "defaultField",
  visceralFatKg: "defaultField",
  visceralFatPercentage: "defaultField",
  bodyCellMassKg: "additionalField",
  intraCellularWaterLiter: "additionalField",
  bodyWaterPercentage: "additionalField",
  intraCellularWaterPercentage: "additionalField",
  boneMassPercentage: "additionalField",
  residualMassKg: "additionalField",
  extraCellularWaterLiter: "additionalField",
  totalBodyWaterLiter: "additionalField",
  extraCellularWaterPercentage: "additionalField",
  residualMassPercentage: "additionalField",
};

const bodyCompositionHistoryTableQuery = graphql(`
  query BodyCompositionsHistoryTable($id: ID!) {
    client(id: $id) {
      id
      setting {
        id
        bodyCompositionFields {
          fieldName
          fieldLabel
          visible
        }
      }
      bodyCompositionsByDay {
        nodes {
          ...ViewBodyCompositionModal
        }
      }
    }
  }
`);

const deleteBodyCompositionsMutation = graphql(`
  mutation DeleteBodyCompositions2($input: DeleteBodyCompositionsInput!) {
    deleteBodyCompositions(input: $input) {
      client {
        id
      }
    }
  }
`);

const BodyCompositionHistoryTable: React.FC = () => {
  const { classes, cx } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const { data, loading } = useQuery(bodyCompositionHistoryTableQuery, {
    variables: { id: clientId ?? "" },
  });
  const [deleteBodyCompositions] = useMutation(deleteBodyCompositionsMutation);
  const alert = useAlert();
  const snackbar = useGlobalSnackbar();
  const deleteDialog = useDialogState();
  const settings = data?.client?.setting;

  const bodyCompositions = data?.client?.bodyCompositionsByDay?.nodes ?? [];

  const [bcDetailsModal, setbcDetailsModal] = useState<boolean>(false);
  const [addBcModal, setAddBcModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentBcByDay, setCurrentBcByDay] =
    useState<ViewBodyCompositionModalFragment | undefined>(undefined);

  const handleOpenBcDetailsModal = (day: string) => {
    if (isEmpty(bodyCompositions)) return;
    const bc = bodyCompositions.find((node) => node?.day === day);
    if (isEmpty(bc)) return;
    setbcDetailsModal(true);
    setCurrentBcByDay(bc);
  };

  const handleCloseBcDetailsModal = () => {
    setbcDetailsModal(false);
    setCurrentBcByDay(undefined);
  };

  const handleEdit = (date: Date) => {
    setCurrentDate(date);
    setIsEditing(true);
    setAddBcModal(true);
    setbcDetailsModal(false);
  };

  const handleOpenAddBcModal = () => {
    setAddBcModal(true);
  };

  const handleCloseAddBcModal = () => {
    setCurrentDate(new Date());
    setAddBcModal(false);
    setIsEditing(false);
  };

  const getFieldValue = (
    fieldName: string,
    value: Maybe<number>
  ): string | number | null => {
    if (value === null) return null;
    if (endsWith(fieldName, "percentage")) {
      return `${percent(value, { round: 2 })} %`;
    }
    return value;
  };

  const getField = (fieldName: string) => {
    return find(settings?.bodyCompositionFields, {
      fieldName: snakeCase(fieldName),
    });
  };

  const handleDelete = async () => {
    if (isEmpty(currentBcByDay)) return;
    try {
      const result = await deleteBodyCompositions({
        variables: {
          input: { clientId, date: currentBcByDay?.bodyComposition.date ?? "" },
        },
        refetchQueries: [
          {
            query: bodyCompositionHistoryTableQuery,
            variables: { id: clientId ?? "" },
          },
        ],
      });
      if (result.data) alert.open("Body measurements deleted successfully.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while deleting body measurements. Please try again later."
        )
      );
    }
  };

  const _rows = () => {
    return bodyCompositions?.map((node) => {
      if (!node) return null;
      const date = node.day ? format(new Date(node.day), "dd MMM, yyyy") : "-";
      const time = node.bodyComposition.createdAt
        ? format(new Date(node.bodyComposition.createdAt), "hh:mm bb")
        : "-";
      return (
        <StyledTableRow key={node.day}>
          <StyledTableCell
            className={classes.stickyLeftCell}
            component="th"
            scope="row"
          >
            <Typography variant="body2" className={classes.cellLabel}>
              {date}
            </Typography>
            <span className={classes.textSecondary}>{time}</span>
          </StyledTableCell>
          {Object.entries(
            omit(node.bodyComposition, ["createdAt", "__typename"])
          ).map(([key, value]) => {
            const field = getField(key);
            if (field === undefined || value === undefined) return;
            if (typeof value == "string") return;
            const fieldValue = getFieldValue(field?.fieldName, value);
            const isDefault = FIELDS[key] === "defaultField";
            if (snakeCase(key) === field.fieldName) {
              if (isDefault) {
                return (
                  <StyledTableCell align="center" key={key}>
                    <Typography variant="body2" className={classes.cellLabel}>
                      {fieldValue == null ? "-" : fieldValue}
                    </Typography>
                  </StyledTableCell>
                );
              } else {
                if (field.visible) {
                  return (
                    <StyledTableCell align="center" key={key}>
                      <Typography variant="body2" className={classes.cellLabel}>
                        {fieldValue == null ? "-" : fieldValue}
                      </Typography>
                    </StyledTableCell>
                  );
                }
              }
            }
          })}
          <StyledTableCell align="center" className={classes.moreCell}>
            <OptionMenu
              onOpen={() => handleOpenBcDetailsModal(node.day)}
              onDelete={() => {
                setCurrentBcByDay(node);
                deleteDialog.show();
              }}
            />
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  const _tableHeads = () => {
    if (bodyCompositions == null) return;
    const node = bodyCompositions[0];
    if (!node) return;

    return Object.entries(
      omit(node.bodyComposition, ["__typename", "createdAt"])
    ).map(([key], index) => {
      const field = getField(key);
      if (field === undefined) return;
      const isDefault = FIELDS[key] === "defaultField";
      if (snakeCase(key) === field.fieldName) {
        if (isDefault) {
          return (
            <StyledTableCellHead key={index}>
              {field.fieldLabel}
            </StyledTableCellHead>
          );
        } else {
          if (field.visible) {
            return (
              <StyledTableCellHead key={index}>
                {field.fieldLabel}
              </StyledTableCellHead>
            );
          }
        }
      }
    });
  };

  if (loading) return null;

  return (
    <>
      <Box className={classes.root}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography className={classes.headerText}>
            Body Composition
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <StyledButton
              variant={"outlined"}
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddBcModal}
            >
              Add
            </StyledButton>
          </Box>
        </Box>
        <TableContainer>
          <Table className={classes.table}>
            <StyledTableHead>
              <StyledTableHeadRow>
                {!isEmpty(bodyCompositions) && (
                  <StyledTableCellHead
                    className={cx(classes.dateLabel, classes.stickyLeftCell)}
                    height="5%"
                    align="center"
                  >
                    Date and Time
                  </StyledTableCellHead>
                )}
                {_tableHeads()}
              </StyledTableHeadRow>
            </StyledTableHead>
            <TableBody>{_rows()}</TableBody>
          </Table>
        </TableContainer>
        {isEmpty(bodyCompositions) && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Typography variant="h6" color="textSecondary">
              This client does not have any entries yet
            </Typography>
          </Box>
        )}
      </Box>
      <ViewBodyCompositionModal
        settings={settings}
        bcByDay={currentBcByDay}
        visibility={bcDetailsModal}
        onEdit={handleEdit}
        onClose={handleCloseBcDetailsModal}
        onDelete={deleteDialog.show}
      />
      <AddBodyCompositionModal
        isEditing={isEditing}
        currentDate={currentDate}
        settings={settings}
        visibility={addBcModal}
        onClose={handleCloseAddBcModal}
      />
      <DeleteModal
        visible={deleteDialog.shown}
        title=""
        message="Are you sure you want to delete body compositions for this date?"
        onDelete={() => {
          handleDelete();
          handleCloseBcDetailsModal();
        }}
        onClose={deleteDialog.hide}
      />
    </>
  );
};

export default BodyCompositionHistoryTable;
